/**
 * Master Data Service
 * Communicates with Electron main process via IPC to manage master data in NeDB
 * Implements local-first strategy: always read from NeDB first
 * 
 * Note: window.api type definitions are in src/utils/printing/types.d.ts
 */

// Map Firestore collection names to NeDB collection names
const COLLECTION_MAP: { [key: string]: string } = {
  // Direct mappings
  stores: 'stores',
  items: 'items',
  menus: 'menus', // New menu system (multiple menus per store)
  categories: 'categories', // New category system (categories linked to menus)
  taxes: 'taxes',
  customFields: 'customFields',
  advancedOption: 'advancedOptions', // Firestore uses 'advancedOption' (singular), NeDB uses 'advancedOptions' (plural)
  topMenus: 'topMenus', // Legacy top menus (kept for backward compatibility)
  ruleManager: 'ruleManager',
  customers: 'customers',
  itemTags: 'tags', // Firestore uses 'itemTags', NeDB uses 'tags'
  printerSettings: 'printerSettings',
  webhooks: 'webhooks',
  tables: 'tables',
  serviceFees: 'serviceFees',
  feesDiscounts: 'feesDiscounts',
  paymentMethods: 'paymentMethods',
  openingHours: 'openingHours',
  images: 'images',
};

class MasterDataService {
  /**
   * Get NeDB collection name from Firestore collection name
   */
  private getCollectionName(firestoreCollection: string): string {
    return COLLECTION_MAP[firestoreCollection] || firestoreCollection;
  }

  /**
   * Save master data to NeDB (local-first)
   */
  async saveMasterData(
    collection: string,
    docId: string,
    data: any
  ): Promise<any> {
    try {
      const nedbCollection = this.getCollectionName(collection);
      const result = await window.api.invoke(
        'master-data:insert',
        nedbCollection,
        docId,
        data
      );
      if (result?.error) {
        throw new Error(result.error);
      }
      return result;
    } catch (error) {
      console.error(`[MasterDataService] Error saving ${collection}/${docId}:`, error);
      throw error;
    }
  }

  /**
   * Get master data from NeDB (local-first)
   */
  async getMasterData(collection: string, docId: string): Promise<any | null> {
    try {
      const nedbCollection = this.getCollectionName(collection);
      const result = await window.api.invoke(
        'master-data:find-one',
        nedbCollection,
        docId
      );
      if (result?.error) {
        throw new Error(result.error);
      }
      return result;
    } catch (error) {
      console.error(`[MasterDataService] Error getting ${collection}/${docId}:`, error);
      return null;
    }
  }

  /**
   * Get all master data for a collection from NeDB
   */
  async getAllMasterData(collection: string): Promise<any[]> {
    try {
      const nedbCollection = this.getCollectionName(collection);
      const result = await window.api.invoke(
        'master-data:find-all',
        nedbCollection
      );
      if (result?.error) {
        throw new Error(result.error);
      }
      return Array.isArray(result) ? result : [];
    } catch (error) {
      console.error(`[MasterDataService] Error getting all ${collection}:`, error);
      return [];
    }
  }

  /**
   * Delete master data from NeDB
   */
  async deleteMasterData(collection: string, docId: string): Promise<boolean> {
    try {
      const nedbCollection = this.getCollectionName(collection);
      const result = await window.api.invoke(
        'master-data:remove',
        nedbCollection,
        docId
      );
      if (result?.error) {
        throw new Error(result.error);
      }
      return result?.success === true;
    } catch (error) {
      console.error(`[MasterDataService] Error deleting ${collection}/${docId}:`, error);
      return false;
    }
  }

  /**
   * Clear all documents in a collection
   */
  async clearCollection(collection: string): Promise<boolean> {
    try {
      const nedbCollection = this.getCollectionName(collection);
      const result = await window.api.invoke(
        'master-data:clear',
        nedbCollection
      );
      if (result?.error) {
        throw new Error(result.error);
      }
      return result?.success === true;
    } catch (error) {
      console.error(`[MasterDataService] Error clearing ${collection}:`, error);
      return false;
    }
  }

  /**
   * Get sync status for all collections
   */
  async getSyncStatus(): Promise<{ [collection: string]: { count: number; lastUpdated: string | null } }> {
    try {
      const result = await window.api.invoke('master-data:get-sync-status');
      if (result?.error) {
        throw new Error(result.error);
      }
      return result || {};
    } catch (error) {
      console.error('[MasterDataService] Error getting sync status:', error);
      return {};
    }
  }

  /**
   * Check if collection is master data
   */
  isMasterDataCollection(collection: string): boolean {
    return collection in COLLECTION_MAP || 
           !['orders', 'sessions', 'transactions', 'activeSessions'].includes(collection);
  }

  /**
   * Get list of all master data collections
   */
  getMasterDataCollections(): string[] {
    return Object.keys(COLLECTION_MAP);
  }
}

// Singleton instance
const masterDataService = new MasterDataService();

export default masterDataService;
